import { WbmMainHeaderApi } from "@wbmason/main-header-api";
import { setShoppingLocationLoading } from "@wbmason/main-header-api/wbm-main-header-api/src/WbmMainHeaderApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { of, Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import { IAddress } from "../../interfaces/IAddress";
import { IAddressBudget } from "../../interfaces/IAddressBudget";
import { IDefaultAddress } from "../../interfaces/IDefaultAddress";
import { IDisplayState } from "../../interfaces/IDisplayState";
import { IShoppingLocation } from "../../interfaces/IShoppingLocation";
import { IWorkflow } from "../../interfaces/IWorkflow";
import { AddressesService } from "../../utilities/addressesService";
import { CartService } from "../../utilities/cartService";
import ConfigPropService from "../../utilities/configPropService";
import DigitalDataService from "../../utilities/digitalDataService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import ScrollBarService from "../../utilities/scrollBarService";
import ServerSideService from "../../utilities/serverSideService";
import UserService from "../../utilities/userService";
import Icon from "../icon/icon";
import StorageService from "./../../utilities/storageService";
import FormattedAddress from "./formattedAddress";
import InsufficientAuthAddressSelectWrapper from "./insufficientAuthAddressSelectWrapper";
import WbmSelectAddressWrapper from "./WbmSelectAddressWrapper";
import EnvironmentService from "../../utilities/environmentService";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

const wbmCBBudgetType = {
  CostCenter: 0,
  UserConfig: 10,
  DeliveryAddress: 20,
  None: -1,
};

export interface IAddressSelectProps {
  isLoggedIn: boolean;
  isCookied: boolean;
  renderTrigger: (currentFormattedAddress: React.ReactNode) => React.ReactNode;
  budgetCallback?: (addressBudget: IAddressBudget) => void;
  workflow: IWorkflow;
  isSecondarySlideIn: boolean;
  displayState: IDisplayState;
  shoppingLocation?: IShoppingLocation;
  setShoppingLocation: (shoppingLocation: IShoppingLocation) => void;
  isSecondary?: boolean;
  [key: string]: unknown;
}

let previousAddress: IDefaultAddress | null = null;

function AddressSelect({
  isLoggedIn,
  isCookied,
  renderTrigger,
  budgetCallback,
  workflow,
  isSecondarySlideIn,
  displayState,
  setShoppingLocation,
  ...props
}: IAddressSelectProps) {
  const [showAvailableBudget, setShowAvailableBudget] = useState(false);
  const [budgetType, setBudgetType] = useState(wbmCBBudgetType.None);
  const [navTokens, setNavTokens] = useState<Record<string, string> | null>(
    null,
  );
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [manageShippingAddresses, setManageShippingAddresses] = useState(false);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [usePaging, setUsePaging] = useState(true);
  const [useExternalIdentityProvider, setUseExternalIdentityProvider] =
    useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [responsiveManageAddresses, setResponsiveManageAddresses] =
    useState(false);

  const isSelectedAddress = useMemo(() => {
    return (
      !!props.shoppingLocation &&
      (!!props.shoppingLocation.addressRecID || !!props.shoppingLocation.id)
    );
  }, [props.shoppingLocation]);

  const formattedAddress = useMemo(() => {
    return !props.shoppingLocation ? null : (
      <FormattedAddress
        shoppingLocation={props.shoppingLocation}
        isSelectedAddress={isSelectedAddress}
      />
    );
  }, [props.shoppingLocation, isSelectedAddress]);

  const addressRecIdToString = useCallback(
    (addressRecId: string | number | undefined) => {
      if (typeof addressRecId === "number") {
        return addressRecId.toString();
      }
      return addressRecId || "";
    },
    [],
  );

  const currentAddressId = useMemo(() => {
    return addressRecIdToString(
      isSelectedAddress
        ? props.shoppingLocation?.addressRecID
          ? props.shoppingLocation?.addressRecID
          : props.shoppingLocation?.id
        : "",
    );
  }, [addressRecIdToString, isSelectedAddress, props.shoppingLocation]);

  const getFloat = useCallback((value: string) => {
    const float = parseFloat(value.replace("$", "").replace(/,/g, ""));
    return float;
  }, []);

  const updateBudget = useCallback(
    (address: IAddress) => {
      if (budgetCallback && address && address.budget) {
        budgetCallback({
          availableBudget: getFloat(address.budget.formattedTotal),
          remainingBudget: getFloat(address.budget.formattedAvailableRemaining),
          formattedAvailableRemaining:
            address.budget.formattedAvailableRemaining,
          formattedTotal: address.budget.formattedTotal,
        });
      }
    },
    [budgetCallback, getFloat],
  );

  const loadAddresses = useCallback(
    (shouldUpdateShoppingLocation = false) => {
      if (isLoggedIn || isCookied) {
        let shoppingLocationSub: Subscription | null = null;

        const subscription = AddressesService.getDeliveryAddresses().subscribe(
          (response: IAddress[]) => {
            setAddressesLoaded(true);
            setAddresses(response);

            if (response.length < 100) {
              setUsePaging(false);
            }

            // only update shopping location when explicitly requested (from refresh)
            if (shouldUpdateShoppingLocation && props.shoppingLocation) {
              const selectedAddress = response.find(
                (addr) =>
                  addr.displayId ===
                    props.shoppingLocation?.mbsAltAddressRecId &&
                  addr.id === props.shoppingLocation?.addressRecID?.toString(),
              );

              if (selectedAddress) {
                const defaultAddress: IDefaultAddress = {
                  addressRecId: selectedAddress.id,
                  mbsAltAddressRecId: selectedAddress.displayId,
                  addressName: selectedAddress.name,
                  attention: selectedAddress.attention,
                  phoneNumber: selectedAddress.phoneNumber,
                  zipCode: selectedAddress.zipCode,
                  street: selectedAddress.street,
                  state: selectedAddress.state,
                  city: selectedAddress.city,
                  dlvModeId: selectedAddress.dlvMode,
                };

                shoppingLocationSub = AddressesService.setShoppingLocation(
                  defaultAddress,
                ).subscribe(
                  (updatedShoppingLocation) => {
                    setShoppingLocation(updatedShoppingLocation);
                  },
                  (error) => {
                    console.error("Failed to update shopping location:", error);
                  },
                );
              } else {
                // selected address not found (deleted), clear cache and get latest shopping location
                AddressesService.clearShoppingLocationCache();
                shoppingLocationSub =
                  AddressesService.getShoppingLocation().subscribe(
                    (shoppingLocation) => {
                      setShoppingLocation(shoppingLocation);
                    },
                    (error) => {
                      console.error("Failed to get shopping location:", error);
                    },
                  );
              }
            }
          },
        );

        return {
          unsubscribe: () => {
            subscription.unsubscribe();
            if (shoppingLocationSub) {
              shoppingLocationSub.unsubscribe();
            }
          },
        } as Subscription;
      }
      return null;
    },
    [isCookied, isLoggedIn, props.shoppingLocation, setShoppingLocation],
  );

  useEffect(() => {
    const configPropSub = ConfigPropService.get([
      ConfigProps.ShowAvailableBudget,
      ConfigProps.ManageShippingAddresses,
      ConfigProps.UseExternalIdentityProvider,
      ConfigProps.EditBillingAddress,
      ConfigProps.EnableResponsiveManageAddresses,
    ]).subscribe((configProp) => {
      setShowAvailableBudget(
        ConfigPropService.isYesOrTrue(
          configProp[ConfigProps.ShowAvailableBudget],
        ),
      );
      setManageShippingAddresses(
        configProp[ConfigProps.ManageShippingAddresses].toLowerCase() !==
          "none" ||
          ConfigPropService.isYesOrTrue(
            configProp[ConfigProps.EditBillingAddress],
          ),
      );
      setUseExternalIdentityProvider(
        ConfigPropService.isYesOrTrue(
          configProp[ConfigProps.UseExternalIdentityProvider],
        ),
      );
      setResponsiveManageAddresses(
        ConfigPropService.isYesOrTrue(
          configProp[ConfigProps.EnableResponsiveManageAddresses],
        ),
      );
    });

    const budgetTypeSub = (
      DigitalDataService.userConfigId()
        ? CartService.getBudgetType()
        : of(wbmCBBudgetType.None)
    ).subscribe((value) => {
      setBudgetType(value);
    });

    const navTokenSub = NavigationTokensService.get([
      NavigationTokens.ManageAddresses,
    ]).subscribe((navTokens) => {
      setNavTokens(navTokens);
    });

    // initial load
    const loadAddressesSub = loadAddresses(false);

    // listen for refresh events
    let refreshAddressesSub: Subscription | null = null;
    const refreshSub = WbmMainHeaderApi.getAddressRefreshObservable().subscribe(
      () => {
        // clear cache before reloading to ensure fresh data
        AddressesService.clearDeliveryAddressesCache();
        // refresh delivery addresses - update shopping location
        refreshAddressesSub = loadAddresses(true);
      },
    );

    return () => {
      configPropSub.unsubscribe();
      budgetTypeSub.unsubscribe();
      navTokenSub.unsubscribe();
      refreshSub.unsubscribe();

      if (loadAddressesSub) {
        loadAddressesSub.unsubscribe();
      }

      if (refreshAddressesSub) {
        refreshAddressesSub.unsubscribe();
      }
    };
  }, [loadAddresses]);

  useEffect(() => {
    if (addresses && props.shoppingLocation) {
      const address = addresses.filter(
        (x) =>
          (x.id as unknown) ===
          (props.shoppingLocation?.addressRecID as unknown),
      )[0];
      updateBudget(address);
    }
  }, [props.shoppingLocation, addresses, updateBudget]);

  useEffect(() => {
    const openSlideoutSub = WbmMainHeaderApi.getAddressSlideout().subscribe(
      (open) => {
        setForceOpen(open);
      },
    );

    return () => {
      openSlideoutSub.unsubscribe();
    };
  });

  const updateShoppingLocation = useCallback(
    (shoppingLocation: IShoppingLocation) => {
      if (shoppingLocation) {
        setShoppingLocation(shoppingLocation);
      }
    },
    [setShoppingLocation],
  );

  const onAddressUpdate = useCallback(
    (addressId: string) => {
      const newAddress = addresses.find(
        (x) => x.id.toString() === addressId,
      ) as IAddress;
      const defaultAddress: IDefaultAddress = {
        addressRecId: newAddress.id,
        mbsAltAddressRecId: newAddress.displayId,
        addressName: newAddress.name,
        attention: newAddress.attention,
        phoneNumber: newAddress.phoneNumber,
        zipCode: newAddress.zipCode,
        street: newAddress.street,
        state: newAddress.state,
        city: newAddress.city,
        dlvModeId: newAddress.dlvMode,
      };

      if (previousAddress) {
        if (
          previousAddress.addressRecId === defaultAddress.addressRecId &&
          previousAddress.zipCode === defaultAddress.zipCode &&
          previousAddress.dlvModeId === defaultAddress.dlvModeId &&
          previousAddress.city === defaultAddress.city &&
          previousAddress.state === defaultAddress.state &&
          previousAddress.phoneNumber === defaultAddress.phoneNumber &&
          previousAddress.attention === defaultAddress.attention
        ) {
          return false;
        }
      }
      previousAddress = defaultAddress;

      setShoppingLocationLoading(true);

      // this is an anti-pattern. All subscriptions should be disposed when a component unmounts
      AddressesService.setShoppingLocation(defaultAddress).subscribe(
        (shoppingLocation) => {
          // returns 500 is shiptoselection is false
          // ecom api doesn't return a shopping location
          updateShoppingLocation(
            isEcom
              ? {
                  displayId: "",
                  dlvMode: "",
                  addressName: newAddress.name,
                  addressRecID: Number(newAddress.id),
                  attention: newAddress.attention,
                  city: newAddress.city,
                  dlvModeId: newAddress.dlvMode,
                  mbsAltAddressRecId: newAddress.displayId,
                  phoneNumber: newAddress.phoneNumber,
                  state: newAddress.state,
                  street: newAddress.street,
                  zipCode: newAddress.zipCode,
                  id: "",
                  countryCode: "",
                }
              : shoppingLocation,
          );

          setShoppingLocationLoading(false);
        },
        () => {
          setShoppingLocationLoading(false);
        },
      );
    },
    [updateShoppingLocation, addresses],
  );

  const showAddressSelection = () => {
    return isLoggedIn;
  };

  const showLogInForAddresses = () => {
    return isCookied;
  };

  const showLogInForAddressesAndZip = () => {
    return !isCookied && !isLoggedIn;
  };

  const closeCallback = useCallback(() => {
    setForceOpen(false);
  }, [setForceOpen]);

  const manageAddressesUrl = useMemo(() => {
    if (responsiveManageAddresses && manageShippingAddresses) {
      return WBMUrlService.getManageAddressesUrl(
        BaseHostUrlService.get(),
        `${
          responsiveManageAddresses
            ? EnvironmentService.get().ecomVnextUrl
            : EnvironmentService.get().ecom
        }`,
        responsiveManageAddresses,
      );
    }

    return navTokens &&
      navTokens[NavigationTokens.ManageAddresses] &&
      manageShippingAddresses
      ? navTokens[NavigationTokens.ManageAddresses]
      : "";
  }, [navTokens, manageShippingAddresses, responsiveManageAddresses]);

  const isEcom = useMemo(
    () => EnvironmentService.get().isEcom === true,
    [EnvironmentService],
  );

  const slideOutTrigger = useCallback(() => {
    if (addressesLoaded && addresses.length === 0 && isSecondarySlideIn) {
      return (
        <div className="cart-change-address">
          <Icon className="align-middle">location_on</Icon>
          <span>
            {formattedAddress ? (
              <b>{formattedAddress}</b>
            ) : (
              <b>Select An Address</b>
            )}
          </span>
        </div>
      );
    } else {
      return (
        <div>
          <div className="location-icon">
            <Icon className="align-middle">location_on</Icon>
          </div>
          <div className={`delivery-address${isEcom ? " ecom" : ""}`}>
            <span className="d-none d-lg-inline deliver-to">Deliver to</span>
            <br className="d-none d-lg-inline" />
            <b>
              {formattedAddress !== null
                ? formattedAddress
                : "Select An Address"}
            </b>
          </div>
        </div>
      );
    }
  }, [
    addressesLoaded,
    addresses,
    formattedAddress,
    isSecondarySlideIn,
    isEcom,
  ]);

  useEffect(() => {
    if (!previousAddress && addresses && addresses.length === 1) {
      onAddressUpdate(addresses[0].id);
    }
  }, [addresses, onAddressUpdate]);

  if (
    !displayState.displayAddressWhileShopping ||
    !UserService.displayUserInfo()
  ) {
    return <></>;
  }

  if (ServerSideService.isServerSide) {
    return slideOutTrigger();
  }

  if (showAddressSelection()) {
    return (
      <WbmSelectAddressWrapper
        fallback={slideOutTrigger()}
        addresses={addresses}
        manageAddressesUrl={manageAddressesUrl}
        selectedAddressId={
          typeof currentAddressId === "number"
            ? (currentAddressId as number).toString()
            : currentAddressId
        }
        onSelectedAddressChanged={onAddressUpdate}
        showAvailableBudget={showAvailableBudget}
        isAddressBudgetType={budgetType === wbmCBBudgetType.DeliveryAddress}
        containerId="body"
        isSecondarySlideIn={isSecondarySlideIn}
        displayDeliveryDisclaimer={true}
        renderTrigger={() => renderTrigger(formattedAddress)}
        storageService={StorageService}
        currentAddress={addresses.filter((x) => x.id === currentAddressId)[0]}
        usePaging={usePaging}
        appendRemoveContainerWidth={(isOpen: boolean) => {
          ScrollBarService.applyModalOpen(isOpen, "address select", true);
        }}
        forceOpen={forceOpen}
        closeCallback={closeCallback}
      />
    );
  } else if (showLogInForAddresses()) {
    return (
      <InsufficientAuthAddressSelectWrapper
        isSecondary={props.isSecondary}
        fallback={slideOutTrigger()}
        formattedAddress={() => renderTrigger(formattedAddress)}
        showZipCodeSection={false}
        useExternalIdentityProvider={useExternalIdentityProvider}
        forceOpen={forceOpen}
        closeCallback={closeCallback}
        shoppingLocation={props.shoppingLocation}
      />
    );
  } else if (showLogInForAddressesAndZip()) {
    return (
      <InsufficientAuthAddressSelectWrapper
        isSecondary={props.isSecondary}
        fallback={slideOutTrigger()}
        formattedAddress={() => renderTrigger(formattedAddress)}
        showZipCodeSection={true}
        forceOpen={forceOpen}
        closeCallback={closeCallback}
        shoppingLocation={props.shoppingLocation}
      />
    );
  } else {
    if (!displayState.showShipToSelection && props.shoppingLocation) {
      return (
        <a className="address-header-link" href={manageAddressesUrl}>
          {slideOutTrigger()}
        </a>
      );
    } else {
      return null;
    }
  }
}
export default AddressSelect;
